import { useMemo } from 'react';
import {
  ResponsiveContainer, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip,
  ReferenceLine, ReferenceArea, LabelList,
} from 'recharts';
import { Box, Typography, Chip, useMediaQuery } from '@mui/material';
import { TrendingUp, TrendingDown, Minus, Plus } from 'lucide-react';

export default function MarkerTrendChart({ markerShort, visitOptions, userEntries, theme, t }) {
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const chartData = useMemo(() => {
    const labPoints = visitOptions
      .filter((v) => v.results?.length)
      .map((v) => {
        const r = v.results.find((r) => r.marker_short === markerShort);
        if (!r || r.value == null) return null;
        return {
          date:     v.visit_date,
          label:    new Date(v.visit_date + 'T00:00:00').toLocaleDateString('en', { month: 'short', day: 'numeric', year: '2-digit' }),
          value:    Number(r.value),
          ref_low:  r.ref_low  != null ? Number(r.ref_low)  : undefined,
          ref_high: r.ref_high != null ? Number(r.ref_high) : undefined,
          status:   r.status,
          unit:     r.unit,
          source:   'lab',
        };
      })
      .filter(Boolean);

    const userPoints = (userEntries || [])
      .filter((e) => e.marker_short === markerShort && e.value != null)
      .map((e) => ({
        date:     e.date,
        label:    new Date(e.date + 'T00:00:00').toLocaleDateString('en', { month: 'short', day: 'numeric', year: '2-digit' }),
        value:    Number(e.value),
        ref_low:  e.ref_low  != null ? Number(e.ref_low)  : undefined,
        ref_high: e.ref_high != null ? Number(e.ref_high) : undefined,
        status:   e.status,
        unit:     e.unit,
        source:   'user',
      }));

    return [...labPoints, ...userPoints].sort((a, b) => new Date(a.date) - new Date(b.date));
  }, [markerShort, visitOptions, userEntries]);

  if (!chartData.length) {
    return (
      <Box sx={{ textAlign: 'center', py: 6 }}>
        <Typography color="text.secondary">{t('bt_chart_no_data')}</Typography>
      </Box>
    );
  }

  const unit = chartData[0]?.unit ?? '';
  const mostRecentWithRef = [...chartData].reverse().find((d) => d.ref_low != null || d.ref_high != null);
  const refLow  = mostRecentWithRef?.ref_low;
  const refHigh = mostRecentWithRef?.ref_high;
  const values  = chartData.map((d) => d.value);
  const allNums = [...values, refLow, refHigh].filter((x) => x != null);
  const span    = Math.max(...allNums) - Math.min(...allNums) || 1;
  const pad     = span * 0.3;
  const yMin    = parseFloat((Math.min(...allNums) - pad).toFixed(4));
  const yMax    = parseFloat((Math.max(...allNums) + pad).toFixed(4));

  const green   = theme.palette.traffic.green;
  const red     = theme.palette.traffic.red;
  const primary = theme.palette.primary.main;

  const CustomDot = ({ cx, cy, payload }) => {
    const color = payload.status?.toUpperCase() === 'NORMAL' ? green : red;
    if (payload.source === 'user') {
      return (
        <g transform={`translate(${cx},${cy})`}>
          <rect x={-6} y={-6} width={12} height={12} fill={color} stroke={theme.palette.background.paper} strokeWidth={2} transform="rotate(45)" />
        </g>
      );
    }
    return <circle cx={cx} cy={cy} r={6} fill={color} stroke={theme.palette.background.paper} strokeWidth={2} />;
  };

  const ValueLabel = ({ x, y, value }) => (
    <text x={x} y={y - 13} textAnchor="middle" fontSize={11} fontWeight={700} fill={theme.palette.text.primary}>
      {Number(value).toFixed(2)}
    </text>
  );

  return (
    <Box sx={{ width: '100%', pt: 0, px: 0 }}>
      <Box sx={{ mb: { xs: 1.5, sm: 2.5 } }}>
        <Typography variant="subtitle1" color="text.secondary" sx={{ lineHeight: 1.3 }}>{t('bt_chart_sub1')}</Typography>
        <Typography variant="subtitle1" color="text.secondary" sx={{ lineHeight: 1.3 }}>{t('bt_chart_sub2')}</Typography>
        {unit && (
          <Chip label={`${t('bt_chart_unit')}: ${unit}`} size="small" sx={{ fontWeight: 700, fontSize: { xs: '0.68rem', sm: '0.8rem' }, letterSpacing: 0.3, mt: 1 }} />
        )}
      </Box>
      <ResponsiveContainer width="100%" {...(isMobile ? { aspect: 1.3 } : { height: 340 })}>
        <LineChart data={chartData} margin={{ top: 30, right: isMobile ? 12 : 28, left: isMobile ? -4 : 4, bottom: 8 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} vertical={false} />
          {refLow != null && refHigh != null && (
            <ReferenceArea y1={refLow} y2={refHigh} fill={green} fillOpacity={0.07} stroke="none"
              label={{ value: t('bt_chart_ref_range'), position: 'insideTopRight', fontSize: 10, fill: green, fontWeight: 600 }}
            />
          )}
          {refLow  != null && <ReferenceLine y={refLow}  stroke={green} strokeDasharray="5 3" strokeWidth={1.5} />}
          {refHigh != null && <ReferenceLine y={refHigh} stroke={green} strokeDasharray="5 3" strokeWidth={1.5} />}
          <XAxis dataKey="label" tick={{ fontSize: isMobile ? 11 : 12, fill: theme.palette.text.secondary, fontWeight: 500 }}
            axisLine={{ stroke: theme.palette.text.primary, strokeWidth: 0.75 }} tickLine={false} padding={{ left: isMobile ? 24 : 32, right: isMobile ? 8 : 32 }} />
          <YAxis domain={[yMin, yMax]} tick={{ fontSize: isMobile ? 11 : 12, fill: theme.palette.text.secondary, fontWeight: 500 }}
            axisLine={{ stroke: theme.palette.text.primary, strokeWidth: 0.75 }} tickLine={false} width={isMobile ? 42 : 60}
            tickFormatter={(v) => Number(v).toFixed(2)} />
          <RechartsTooltip
            contentStyle={{ borderRadius: 10, border: `1px solid ${theme.palette.divider}`, backgroundColor: theme.palette.background.paper, boxShadow: '0 4px 16px rgba(0,0,0,0.12)', fontSize: 13 }}
            formatter={(value) => [`${Number(value).toFixed(2)}${unit ? ` ${unit}` : ''}`, t('bt_chart_value')]}
          />
          <Line type="monotone" dataKey="value" stroke={primary} strokeWidth={2.5}
            dot={<CustomDot />} activeDot={{ r: 9, stroke: primary, strokeWidth: 2 }}>
            <LabelList dataKey="value" content={<ValueLabel />} />
          </Line>
        </LineChart>
      </ResponsiveContainer>

      <Box sx={{ mt: { xs: 1.5, sm: 3 }, borderRadius: 2, overflow: 'hidden', border: '1px solid', borderColor: 'divider' }}>
        <Box sx={{
          display: 'grid', gridTemplateColumns: { xs: '2fr 1fr 1fr 1fr', sm: '2fr 1fr 1fr 1fr 1fr' },
          px: { xs: 1, sm: 2 }, py: 1,
          bgcolor: theme.palette.mode === 'light' ? 'grey.C200' : 'greydark.C800',
        }}>
          <Typography variant="body2" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: 'text.secondary' }}>
            {t('bt_chart_col_date')}
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: 'text.secondary' }}>
            {t('bt_chart_col_value')}
          </Typography>
          <Typography variant="body2" sx={{ display: { xs: 'none', sm: 'block' }, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: 'text.secondary' }}>
            {t('bt_chart_col_unit')}
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: 'text.secondary' }}>
            {t('bt_chart_col_trend')}
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: 'text.secondary' }}>
            {t('bt_chart_col_status')}
          </Typography>
        </Box>
        {[...chartData].reverse().map((row, idx, arr) => {
          const prev  = arr[idx + 1];
          const delta = prev != null ? row.value - prev.value : null;
          const TrendIcon = delta == null ? null : Math.abs(delta) < 0.001 ? Minus : delta > 0 ? TrendingUp : TrendingDown;
          const trendColor = delta == null
            ? theme.palette.text.disabled
            : Math.abs(delta) < 0.001
              ? theme.palette.text.secondary
              : row.status?.toUpperCase() === 'NORMAL' ? green : red;
          const isEven = idx % 2 === 0;
          return (
            <Box key={row.date} sx={{
              display: 'grid', gridTemplateColumns: { xs: '2fr 1fr 1fr 1fr', sm: '2fr 1fr 1fr 1fr 1fr' },
              px: { xs: 1, sm: 2 }, py: 0.9, alignItems: 'center',
              bgcolor: isEven
                ? (theme.palette.mode === 'light' ? 'grey.C50'  : 'greydark.C900')
                : (theme.palette.mode === 'light' ? 'grey.C100' : 'greydark.C900'),
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <Typography variant="body2" fontWeight={600} color={theme.palette.mode === 'light' ? 'greydark.C900' : 'grey.C50'}>
                  {new Date(row.date + 'T00:00:00').toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })}
                </Typography>
                {row.source === 'user' && (
                  <Chip label={<Plus size={10} color="#fff" />} size="small"
                    sx={{ height: 16, bgcolor: theme.palette.secondary.main, '& .MuiChip-label': { px: 0.5, display: 'flex', alignItems: 'center' } }}
                  />
                )}
              </Box>
              <Typography variant="body2" fontWeight={600} sx={{ fontVariantNumeric: 'tabular-nums' }}>
                {row.value.toFixed(2)}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ display: { xs: 'none', sm: 'block' } }}>
                {row.unit ?? '—'}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {TrendIcon
                  ? <TrendIcon size={22} color={trendColor} strokeWidth={2.5} />
                  : <Minus size={22} color={theme.palette.text.disabled} strokeWidth={2} />
                }
              </Box>
              <Typography variant="body2" fontWeight={700}
                sx={{ color: row.status?.toUpperCase() === 'NORMAL' ? green : red, letterSpacing: 0.3 }}>
                {(() => {
                  if (!row.status) return '—';
                  const statusKey = `bt_status_${row.status.toLowerCase().replace(/\s+/g, '_')}`;
                  const label = t(statusKey);
                  return label !== statusKey ? label : row.status;
                })()}
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}
